import {
    Component,
    input,
    output,
    signal,
    computed,
    model,
    OnInit,
    OnDestroy,
    forwardRef,
    TemplateRef,
    effect,
    ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Checkbox as PrimeCheckbox, CheckboxModule } from 'primeng/checkbox';
import {
    CheckboxConfig,
    CheckboxChangeEvent,
} from './checkbox.model';

@Component({
    selector: 'app-checkbox',
    standalone: true,
    imports: [CommonModule, FormsModule, CheckboxModule],
    templateUrl: './checkbox.component.html',
    styleUrls: ['./checkbox.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CheckboxComponent),
            multi: true,
        },
    ],
})
export class CheckboxComponent implements OnInit, OnDestroy, ControlValueAccessor {
    // Model binding for checked value (can be boolean, or array for checkbox groups)
    value = model<any>(null);

    // Input signals for configuration
    config = input<CheckboxConfig>({});

    // For checkbox groups: the value of this specific checkbox option
    checkboxValue = input<any>(undefined);

    // Custom templates
    checkboxIconTemplate = input<TemplateRef<any> | null>(null);
    labelTemplate = input<TemplateRef<any> | null>(null);

    // Output events
    onChange = output<CheckboxChangeEvent>();

    // Internal state
    checked = signal<boolean>(false);
    isFocused = signal<boolean>(false);

    @ViewChild('checkbox') private checkbox?: PrimeCheckbox;

    // ControlValueAccessor implementation
    private onChangeFn: (value: any) => void = () => { };
    private onTouchedFn: () => void = () => { };

    // Computed values
    checkboxConfig = computed(() => {
        const defaultConfig: CheckboxConfig = {
            binary: true,
            disabled: false,
            readonly: false,
            label: '',
            name: '',
            inputId: this.generateId(),
            tabindex: 0,
            checkboxIcon: 'pi pi-check',
            trueValue: true,
            falseValue: false,
            variant: 'outlined',
            size: 'medium',
        };
        return { ...defaultConfig, ...this.config() };
    });

    // Generate unique ID for checkbox
    private generateId(): string {
        return `app-checkbox-${Math.random().toString(36).substring(2, 11)}`;
    }

    // Computed for checkbox state
    isChecked = computed(() => {
        const config = this.checkboxConfig();
        const val = this.value();
        const checkboxVal = this.checkboxValue();

        // If checkboxValue is provided, this is part of a checkbox group
        if (checkboxVal !== undefined) {
            return Array.isArray(val) && val.includes(checkboxVal);
        }

        if (config.binary) {
            return val === config.trueValue;
        } else {
            // For array values (non-binary mode)
            return Array.isArray(val) && val.length > 0;
        }
    });

    // Computed for disabled state
    isDisabled = computed(() => {
        return this.checkboxConfig().disabled || false;
    });

    // Computed for readonly state
    isReadonly = computed(() => {
        return this.checkboxConfig().readonly || false;
    });

    // Computed for styling classes
    computedStyleClass = computed(() => {
        const config = this.checkboxConfig();
        const classes = [];

        if (config.styleClass) {
            classes.push(config.styleClass);
        }

        if (config.size) {
            classes.push(`checkbox-${config.size}`);
        }

        if (config.variant) {
            classes.push(`checkbox-${config.variant}`);
        }

        return classes.join(' ');
    });

    constructor() {
        // Effect to sync checked signal with value
        effect(() => {
            this.checked.set(this.isChecked());
        });
    }

    private syncPrimeCheckbox(): void {
        const modelValue = this.checkboxValue() !== undefined ? this.value() : this.checked();
        Promise.resolve().then(() => this.checkbox?.writeValue(modelValue));
    }

    ngOnInit(): void {
        // Initialize component
    }

    ngOnDestroy(): void {
        // Cleanup if needed
    }

    /**
     * Handle checkbox change event
     */
    handleChange(event: any): void {
        const config = this.checkboxConfig();

        // Prevent change if readonly
        if (this.isReadonly()) {
            return;
        }

        const checkboxVal = this.checkboxValue();

        // If checkboxValue is provided, PrimeNG handles the array automatically with two-way binding
        // We just need to notify Angular forms and emit the event
        if (checkboxVal !== undefined) {
            const newValue = this.value();

            // Notify Angular forms of the change
            this.onChangeFn(newValue);
            this.onTouchedFn();

            // Emit the change event
            this.onChange.emit({
                checked: newValue,
                originalEvent: event.originalEvent,
                checkboxComponent: this,
            });
        } else {
            // Binary mode: toggle between trueValue and falseValue
            let newValue: any;

            if (config.binary) {
                newValue = event.checked ? config.trueValue : config.falseValue;
            } else {
                // Array mode: update array
                newValue = event.checked || [];
            }

            // Update the internal value
            this.value.set(newValue);
            this.checked.set(event.checked);

            // Notify Angular forms of the change
            this.onChangeFn(newValue);
            this.onTouchedFn();

            // Emit the change event
            this.onChange.emit({
                checked: newValue,
                originalEvent: event.originalEvent,
                checkboxComponent: this,
            });
        }
    }


    /**
     * Public method to toggle checkbox state
     */
    toggle(): void {
        if (this.isDisabled() || this.isReadonly()) {
            return;
        }

        const config = this.checkboxConfig();
        const currentValue = this.value();
        const newChecked = !this.isChecked();

        if (config.binary) {
            const newValue = newChecked ? config.trueValue : config.falseValue;
            this.value.set(newValue);
            this.checked.set(newChecked);
            this.onChangeFn(newValue);
        } else {
            const newValue = newChecked ? [true] : [];
            this.value.set(newValue);
            this.checked.set(newChecked);
            this.onChangeFn(newValue);
        }

        this.syncPrimeCheckbox();
    }

    /**
     * Public method to check the checkbox
     */
    check(): void {
        if (this.isDisabled() || this.isReadonly() || this.isChecked()) {
            return;
        }

        const config = this.checkboxConfig();
        const newValue = config.binary ? config.trueValue : [true];
        this.value.set(newValue);
        this.checked.set(true);
        this.onChangeFn(newValue);
        this.syncPrimeCheckbox();
    }

    /**
     * Public method to uncheck the checkbox
     */
    uncheck(): void {
        if (this.isDisabled() || this.isReadonly() || !this.isChecked()) {
            return;
        }

        const config = this.checkboxConfig();
        const newValue = config.binary ? config.falseValue : [];
        this.value.set(newValue);
        this.checked.set(false);
        this.onChangeFn(newValue);
        this.syncPrimeCheckbox();
    }

    // ControlValueAccessor methods
    /**
     * Write a new value to the element (called by Angular forms)
     */
    writeValue(value: any): void {
        this.value.set(value);

        const config = this.checkboxConfig();
        const checkboxVal = this.checkboxValue();

        // If checkboxValue is provided, check if it's in the array
        if (checkboxVal !== undefined) {
            this.checked.set(Array.isArray(value) && value.includes(checkboxVal));
        } else if (config.binary) {
            this.checked.set(value === config.trueValue);
        } else {
            this.checked.set(Array.isArray(value) && value.length > 0);
        }
        this.syncPrimeCheckbox();
    }

    /**
     * Register a callback function to be called when the control's value changes
     */
    registerOnChange(fn: (value: any) => void): void {
        this.onChangeFn = fn;
    }

    /**
     * Register a callback function to be called when the control is touched
     */
    registerOnTouched(fn: () => void): void {
        this.onTouchedFn = fn;
    }

    /**
     * Set the disabled state of the control
     */
    setDisabledState(isDisabled: boolean): void {
        // Note: The disabled state should be managed through the config input
        // This method is required by ControlValueAccessor interface
        // The parent component should update the config.disabled property
    }
}

